/**
 * Automation Title Component
 *
 * Standalone Lit component for inline title editing on the edgeless canvas.
 * Owns its own state so parent re-renders (from Gfx selection changes)
 * do NOT destroy the focused input.
 *
 * Pattern copied from: blocksuite/affine/blocks/database/src/components/title/index.ts
 */

import { stopPropagation } from '@blocksuite/affine-shared/utils';
import { SignalWatcher, WithDisposable } from '@blocksuite/global/lit';
import { ShadowlessElement } from '@blocksuite/std';
import type { Text } from '@blocksuite/store';
import { signal } from '@preact/signals-core';
import { css, html } from 'lit';
import { property, query } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { styleMap } from 'lit/directives/style-map.js';

export class AutomationTitle extends SignalWatcher(
  WithDisposable(ShadowlessElement)
) {
  static override styles = css`
    .affine-automation-title {
      position: relative;
      flex: 1;
      font-family: inherit;
      font-size: 14px;
      font-weight: 600;
      color: var(--streamlain-text-primary-color);
      overflow: hidden;
    }

    .affine-automation-title textarea {
      font-size: inherit;
      line-height: inherit;
      font-weight: inherit;
      letter-spacing: inherit;
      font-family: inherit;
      border: none;
      background-color: transparent;
      padding: 0;
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      right: 0;
      outline: none;
      resize: none;
      scrollbar-width: none;
    }

    .affine-automation-title .text {
      user-select: none;
      opacity: 0;
      white-space: pre-wrap;
    }

    .affine-automation-title[data-title-focus='false'] textarea {
      opacity: 0;
    }

    .affine-automation-title[data-title-focus='false'] .text {
      text-overflow: ellipsis;
      overflow: hidden;
      opacity: 1;
      white-space: pre;
    }

    .affine-automation-title [data-title-empty='true']::before {
      content: 'New Automation';
      position: absolute;
      pointer-events: none;
      color: var(--streamlain-text-primary-color);
    }

    .affine-automation-title [data-title-focus='true']::before {
      color: var(--streamlain-placeholder-color);
    }
  `;

  private readonly compositionEnd = () => {
    this.titleText.replace(0, this.titleText.length, this.input.value);
  };

  private readonly onBlur = () => {
    this.isFocus$.value = false;
  };

  private readonly onFocus = () => {
    this.isFocus$.value = true;
  };

  private readonly onInput = (e: InputEvent) => {
    this.text$.value = this.input.value;
    if (!e.isComposing) {
      this.titleText.replace(0, this.titleText.length, this.input.value);
    }
  };

  private readonly onKeyDown = (event: KeyboardEvent) => {
    event.stopPropagation();
    if (event.key === 'Enter' && !event.isComposing) {
      event.preventDefault();
      this.input.blur();
    }
  };

  updateText = () => {
    if (!this.isFocus$.value) {
      this.input.value = this.titleText.toString();
      this.text$.value = this.input.value;
    }
  };

  override connectedCallback() {
    super.connectedCallback();
    requestAnimationFrame(() => {
      this.updateText();
    });
    if (this.titleText?.yText) {
      this.titleText.yText.observe(this.updateText);
      this.disposables.add(() => {
        this.titleText?.yText?.unobserve(this.updateText);
      });
    }
  }

  override render() {
    const isEmpty = !this.text$.value;

    const classList = classMap({
      'affine-automation-title': true,
      ellipsis: !this.isFocus$.value,
    });
    const untitledStyle = styleMap({
      height: isEmpty ? 'auto' : 0,
      opacity: isEmpty && !this.isFocus$.value ? 1 : 0,
    });
    return html` <div
      class="${classList}"
      data-title-empty="${isEmpty}"
      data-title-focus="${this.isFocus$.value}"
    >
      <div class="text" style="${untitledStyle}">New Automation</div>
      <div class="text">${this.text$.value}</div>
      <textarea
        style="${styleMap({
          pointerEvents: this.editing ? 'auto' : 'none',
        })}"
        .disabled="${this.readonly}"
        @pointerdown="${this.editing ? stopPropagation : undefined}"
        @input="${this.onInput}"
        @keydown="${this.onKeyDown}"
        @copy="${stopPropagation}"
        @paste="${stopPropagation}"
        @focus="${this.onFocus}"
        @blur="${this.onBlur}"
        @compositionend="${this.compositionEnd}"
        title="${this.titleText.toString()}"
      ></textarea>
    </div>`;
  }

  @query('textarea')
  private accessor input!: HTMLTextAreaElement;

  private readonly isFocus$ = signal(false);

  private readonly text$ = signal('');

  @property({ attribute: false })
  accessor titleText!: Text;

  @property({ type: Boolean })
  accessor editing: boolean = false;

  @property({ type: Boolean })
  accessor readonly: boolean = false;
}

declare global {
  interface HTMLElementTagNameMap {
    'affine-automation-title': AutomationTitle;
  }
}
